/* eslint-disable @typescript-eslint/no-shadow,prefer-arrow/prefer-arrow-functions */
import { ActivateInput, MfaPurpose, Provider, Tenant, User, UserIdentity, UserLanguageCode } from '@/types'
import { i18n } from '@/setup/setup-i18n'
import * as authService from '@/services/auth'
import * as passwordService from '@/services/password'
import * as activationService from '@/services/activation'
import * as verifyService from '@/services/verify'
import { Beef, client } from '@/services/beef'

interface UserStoreState {
  user: User
  userIdentity: UserIdentity | null
  mfaResetRedirectUri: string | null
  mfaSetupRedirectUri: string | null
  trustedDeviceChallengeId: string | null
}

export const state: UserStoreState = {
  user: {
    email: null,
    password: null,
  },
  userIdentity: null,
  // Used to redirect the user back to the original page when resetting MFA
  mfaResetRedirectUri: null,
  // Used to redirect the user back to the original page when setting up MFA
  mfaSetupRedirectUri: null,
  trustedDeviceChallengeId: null,
}

export const mutations = {
  storeEmail(state: UserStoreState, email: string) {
    state.user = { ...state.user, email }
  },

  storeUserIdentity(state: UserStoreState, userIdentity: UserIdentity) {
    state.userIdentity = { ...state.userIdentity, ...userIdentity }
  },

  storeMfaResetRedirectUri(state: UserStoreState, uri: UserStoreState['mfaResetRedirectUri']) {
    state.mfaResetRedirectUri = uri
  },

  storeMfaSetupRedirectUri(state: UserStoreState, uri: UserStoreState['mfaSetupRedirectUri']) {
    state.mfaSetupRedirectUri = uri
  },

  storeTrustedDeviceChallengeId(state: UserStoreState, challengeId: string | null) {
    state.trustedDeviceChallengeId = challengeId
  },
}

export const actions = {
  authenticateAgainstAuth0(ctx, args: { user: User, tenant: Tenant }) {
    return authService.authenticateAgainstAuth0(args.user, args.tenant, i18n.locale as UserLanguageCode)
  },

  authenticateAgainstAuthX(ctx, args: { code: string, tenantCode: string, userDeviceToken?: string, iid?: string, state?: string, deviceId?: string, temporaryMfaSetup?: boolean }) {
    const { code, tenantCode, userDeviceToken } = args
    return authService.authenticateAgainstAuthX(code, tenantCode, userDeviceToken, args.iid, args.state, args.deviceId, args.temporaryMfaSetup)
  },

  async logout(ctx, args: { token: string, tenant: Tenant, redirectUri: string }) {
    await authService.logoutFromAuthX(args.token).catch((error) => {
      // If the user's token is not available on their device continue to sign them out anyway
      // as the user has already logged out from this device
      // Note: This behaviour is in line Auth0's behaviour for already-logged-out users
      if (error.message !== 'TokenRequired') {
        throw error
      }
    })
    await authService.logoutFromAuth0(args.tenant, args.redirectUri)
  },

  loginViaSso(ctx, args: { tenant: Tenant, applyMfa: boolean, temporaryMfaSetup?: boolean, purpose?: MfaPurpose }) {

    const token = authService.getAuthXToken()

    if (token && token.payload.provider === Provider.authx) {
      return authService.loginViaAuthXSSO(token.value, token.payload.partnerCode)
    }

    return authService.loginViaAuth0SSO(args.tenant, args.applyMfa, args.temporaryMfaSetup, args.purpose)

  },

  requestResetPassword(ctx, email: string) {
    return passwordService.requestResetPassword(email)
  },

  applyResetPassword(ctx, { token, password }: { token: string, password: string }) {
    return passwordService.applyResetPassword(token, password)
  },

  activateUser(ctx, input: ActivateInput) {
    return activationService.activateUser(input)
  },

  validatePerishableToken({ commit }, token: string) {
    return activationService.validatePerishableToken(token).then(({ email }) => {
      commit('storeEmail', email)
    })
  },

  resendActivateToken(ctx, email: string) {
    return activationService.resendActivateToken(email)
  },

  authorizeRedirectUri(ctx, args: { redirectUri: string, tenant: Tenant }) {
    return authService.authorizeRedirectUri(args.tenant, args.redirectUri)
  },

  mfaRegisterTotp(ctx, args: Beef.MfaRegisterTotpRequest['body']) {
    return client.mfaRegisterTotp({ body: args })
  },

  mfaVerifyOtp(ctx, args: Beef.MfaVerifyOtpRequest['body']) {
    return client.mfaVerifyOtp({ body: args })
  },

  mfaReset(ctx, args: Beef.MfaResetRequest['body']) {
    return client.mfaReset({ body: args })
  },

  validateEmailVerificationToken(ctx, args: Beef.ValidateEmailVerificationTokenRequest['body']) {
    return verifyService.validateEmailVerificationToken(args)
  },

  verifyEmail(ctx, args: Beef.VerifyEmailRequest['body']) {
    return verifyService.verifyEmail(args)
  },
  createPasskey(ctx, args: Beef.CreatePasskeyRequest['body']) {
    return client.createPasskey({ body: args })
  },
  verifyPasskeyLogin(ctx, args: Beef.VerifyPasskeyLoginRequest['body']) {
    return client.verifyPasskeyLogin({ body: args })
  },
  createPasskeyState(ctx, args: Beef.CreatePasskeyStateRequest['body']) {
    return client.createPasskeyState({ body: args })
  },
  checkTrustedDeviceStatus(ctx, args: { deviceId: string, token: string }) {
    return client.getTrustedDeviceStatus({ path: { deviceId: args.deviceId }, body: { token: args.token } })
  },
  confirmTrustedDevice(ctx, args: { deviceId: string, email: string, confirmationId: string }) {
    return client.confirmTrustedDevice({ path: { deviceId: args.deviceId }, body: { email: args.email, confirmationId: args.confirmationId } })
  },
  getTrustedDeviceChallenge(ctx, args: { deviceId: string, token: string }) {
    return client.getTrustedDeviceChallenge({ path: { deviceId: args.deviceId }, body: { token: args.token } })
  },
  async verifyTrustedDeviceChallenge({ commit }, args: { deviceId: string, token: string, challengeId: string, signature: string, userDeviceToken?: string }) {
    const result = await client.verifyTrustedDeviceChallenge({ path: { deviceId: args.deviceId }, body: { token: args.token, challengeId: args.challengeId, signature: args.signature, userDeviceToken: args.userDeviceToken } })
    commit('storeTrustedDeviceChallengeId', args.challengeId)
    return result
  },
  createTrustedDevice(ctx, args: Beef.CreateTrustedDeviceRequest['body']) {
    return client.createTrustedDevice({ body: args })
  },
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
}
